import React, { useEffect } from 'react';
import { LANGUAGES, VOICE_LANGUAGE_NAMES } from '../lib/i18n';
import { FOREIGN } from '../lib/money';
import packageInfo from '../../package.json';

// "Kòb pa m 2.5.0 · a0064a4": the release, and the commit this bundle was built
// from (set by the build script; absent under `yarn start`). The commit is what
// tells one deploy from the next.
const VERSION = [`Kòb pa m ${packageInfo.version}`, (process.env.REACT_APP_COMMIT || '').slice(0, 7)].filter(Boolean).join(' · ');

function Row({ id, label, value, onChange, options }) {
  return (
    <div className="settings-row">
      <label className="settings-label" htmlFor={id}>{label}</label>
      <select id={id} className="settings-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
      </select>
    </div>
  );
}

/** The settings sheet — one row per setting, label left, a compact select right (the Mesaj Pa m idiom). */
export default function SettingsSheet({ settings, update, voices, speakers, onClose, t }) {
  // Kreyòl has its own voices; the other languages share a set. Each keeps its own choice.
  const kreyol = settings.voiceLang === 'ht';
  const cast = kreyol ? voices : speakers;
  const chosen = kreyol ? settings.voiceId : settings.speaker;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="settings-scrim" onClick={onClose} />
      <div className="settings-sheet" role="dialog" aria-label={t('settings.title')}>
        <div className="settings-head">
          <button className="settings-close" type="button" onClick={onClose}
            aria-label={t('settings.close')} title={t('settings.close')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <span className="settings-title">{t('settings.title')}</span>
        </div>
        <div className="settings-list">
          <Row id="set-voice-lang" label={t('settings.voiceLang')}
            value={settings.voiceLang}
            onChange={(voiceLang) => update({ voiceLang })}
            options={VOICE_LANGUAGE_NAMES} />
          {cast.length > 0 && (
            <Row id="set-voice" label={t('settings.voice')}
              value={cast.some((v) => v.id === chosen) ? chosen : cast[0].id}
              onChange={(id) => update(kreyol ? { voiceId: id } : { speaker: id })}
              options={cast.map((v) => [v.id, v.name])} />
          )}
          <Row id="set-auto" label={t('settings.autoSpeak')}
            value={settings.autoSpeak ? 'yes' : 'no'}
            onChange={(v) => update({ autoSpeak: v === 'yes' })}
            options={[['yes', t('yes')], ['no', t('no')]]} />
          <Row id="set-foreign" label={t('settings.foreign')}
            value={settings.foreign}
            onChange={(foreign) => update({ foreign })}
            options={FOREIGN.map((c) => [c, `${c.toUpperCase()} · ${t(`cur.${c}`)}`])} />
          <Row id="set-htd" label={t('settings.showHtd')}
            value={settings.showHtd ? 'yes' : 'no'}
            onChange={(v) => update({ showHtd: v === 'yes' })}
            options={[['yes', t('yes')], ['no', t('no')]]} />
          {settings.showHtd && (
            <Row id="set-dola" label={t('settings.bareDola')}
              value={settings.bareDola}
              onChange={(bareDola) => update({ bareDola })}
              options={[['htd', t('cur.htd')], ['usd', t('cur.usd')]]} />
          )}
          <Row id="set-lang" label={t('settings.lang')}
            value={settings.lang}
            onChange={(lang) => update({ lang })}
            options={LANGUAGES} />
          <Row id="set-skin" label={t('settings.skin')}
            value={settings.skin}
            onChange={(skin) => update({ skin })}
            options={[['auto', t('skin.auto')], ['light', t('skin.light')], ['dark', t('skin.dark')]]} />
        </div>
        <p className="settings-version">{VERSION}</p>
      </div>
    </>
  );
}
